/**
 * The organization's skill leaderboard: every skill a chat can use — the
 * agent's own and the organization's library — with how often its chats
 * loaded each one over the last eight weeks, from the workspace server's
 * `/api/amoreg/skill-board`.
 *
 * Same idiom as `state/commandCenter.ts`: a cached atom per organization over
 * plain `fetch` with the session cookie. The server decides the counts and the
 * tenancy; the page only ranks and draws them. A server without the route
 * (an older deployment) reads `unavailable`.
 *
 * @module state/skillBoard
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";

const SkillBoardEntrySchema = Schema.Struct({
  key: Schema.String,
  kind: Schema.Literals(["agent", "library"]),
  id: Schema.NullOr(Schema.String),
  name: Schema.String,
  slug: Schema.String,
  description: Schema.String,
  scope: Schema.Literals(["global", "jurisdiction", "organization"]),
  category: Schema.NullOr(Schema.String),
  jurisdictions: Schema.Array(Schema.String),
  addedAt: Schema.NullOr(Schema.String),
  uses: Schema.Number,
  weekly: Schema.Array(Schema.Number),
});
export type SkillBoardEntry = typeof SkillBoardEntrySchema.Type;

const SkillBoardSchema = Schema.Struct({
  organizationId: Schema.String,
  generatedAt: Schema.String,
  window: Schema.Struct({ since: Schema.String, until: Schema.String, weeks: Schema.Number }),
  skills: Schema.Array(SkillBoardEntrySchema),
});
export type SkillBoard = typeof SkillBoardSchema.Type;

export const decodeSkillBoard = Schema.decodeUnknownOption(SkillBoardSchema);

export type SkillBoardResult =
  | { readonly kind: "ok"; readonly board: SkillBoard }
  /** This server does not have the route. */
  | { readonly kind: "unavailable" }
  | { readonly kind: "forbidden" }
  | { readonly kind: "error" };

/** Reads a response into a result; exported for tests. */
export async function skillBoardResultOf(response: Response): Promise<SkillBoardResult> {
  if (response.status === 401 || response.status === 403) return { kind: "forbidden" };
  const json = (response.headers.get("content-type") ?? "").includes("application/json");
  if (response.status === 404 || (response.ok && !json)) return { kind: "unavailable" };
  if (!response.ok) return { kind: "error" };
  const body: unknown = await response.json().catch(() => null);
  return Option.match(decodeSkillBoard(body), {
    onNone: (): SkillBoardResult => ({ kind: "error" }),
    onSome: (board): SkillBoardResult => ({ kind: "ok", board }),
  });
}

const fetchSkillBoard = (organizationId: string) =>
  Effect.tryPromise(async () => {
    if (organizationId.length === 0) return { kind: "unavailable" } as SkillBoardResult;
    const response = await fetch(
      resolvePrimaryEnvironmentHttpUrl("/api/amoreg/skill-board", { organizationId }),
      { credentials: "include" },
    );
    return skillBoardResultOf(response);
  }).pipe(Effect.orElseSucceed((): SkillBoardResult => ({ kind: "error" })));

const skillBoardRuntime = Atom.runtime(Layer.empty);
const skillBoardAtom = Atom.family((organizationId: string) =>
  skillBoardRuntime
    .atom(fetchSkillBoard(organizationId))
    .pipe(Atom.withLabel(`web-amoreg:skill-board:${organizationId}`)),
);

export interface SkillBoardView {
  readonly result: SkillBoardResult | null;
  readonly isRefreshing: boolean;
  readonly refresh: () => void;
}

export function useSkillBoard(organizationId: string | null): SkillBoardView {
  const atom = useMemo(() => skillBoardAtom(organizationId ?? ""), [organizationId]);
  const state = useAtomValue(atom);
  const result = Option.getOrNull(AsyncResult.value(state));
  const refresh = useCallback(() => appAtomRegistry.refresh(atom), [atom]);
  return { result, isRefreshing: result === null || state.waiting, refresh };
}
